<template>
  <div class="mb-4">
    <StoryboardInstruction
      @regenerate="$emit('regenerate')"
      @input="$emit('update:instruction', $event)"
      :value="instruction"
      :label="label"
      :loading="loading"
      :rows="rows"
      :status="generationStatus"
      :button-icon="nodeId ? 'mdi-refresh' : 'mdi-creation'"
    />

    <div class="video-preview-container">
      <div v-if="!filePreview" class="video-placeholder">
        <v-icon size="48" color="grey lighten-1">mdi-video-outline</v-icon>
        <div class="text-caption text-center mt-2">{{ $t('aiwf_sb_form_no_video') }}</div>
      </div>

      <div v-else class="video-preview">
        <video class="preview-video" ref="videoPlayer" controls>
          <source :src="filePreview" type="video/mp4">
          {{ $t('aiwf_sb_form_video_not_supported') }}
        </video>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import StoryboardInstruction from './StoryboardInstruction.vue'
import RenditionActions from '@/store/content/rendition/actions-types'
import AppActions from '@/store/app/actions-types'
import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  name: 'StoryboardVideoGenerate',

  components: {
    StoryboardInstruction,
  },

  data() {
    return {
      filePreview: null
    }
  },

  props: {
    instruction: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    nodeId: {
      type: [Number, String],
    },
    rows: {
      type: [Number, String],
      default: 3
    },
    maxHeight: {
      type: String,
      default: '80px'
    },
    storyboardData: {
      type: Object,
      default: () => ({})
    },
    sceneIndex: {
      type: Number,
      default: 0
    },
    generationStatus: {
      type: String,
      default: ''
    },
  },

  mounted() {
    if (this.nodeId) {
      this.getFilePreview(this.nodeId)
      this.listenForRendition()
    }
  },

  watch: {
    nodeId(newNodeId) {
      if (newNodeId) {
        this.getFilePreview(newNodeId)
        this.listenForRendition()
      }
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_INFO_MESSAGE,
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapActions('content/rendition', [
      RenditionActions.GET_PREVIEW,
    ]),

    handleNotification(message) {
      if (message.type == 'rendition_service' && message.status == 'done' && message.node_id == this.nodeId) {
        this.getFilePreview(this.nodeId)
        BgActionEventBus.$off('bgAction', this.handleNotification)
      }
    },

    listenForRendition() {
      BgActionEventBus.$off('bgAction', this.handleNotification)
      BgActionEventBus.$on('bgAction', this.handleNotification)
    },

    async getFilePreview(nodeId) {
      const result = await this[RenditionActions.GET_PREVIEW](nodeId)
      this.filePreview = result[0].url || null

      if (this.filePreview && this.$refs.videoPlayer) {
        this.$refs.videoPlayer.load()
      }
    },

  },

  beforeDestroy() {
    BgActionEventBus.$off('bgAction', this.handleNotification)
  },

  emits: ['update:instruction', 'regenerate', 'upload', 'delete', 'uploaded', 'send-message']
}
</script>

<style lang="scss" scoped>
  .video-preview-container {
    position: relative;
    height: 180px;
    border: 1px solid #808080;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
  }

  .video-placeholder {
    text-align: center;
    color: #9e9e9e;
  }

  .video-preview {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .preview-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    border-radius: 6px;
  }

  .placeholder-text {
    margin-top: 8px;
    font-size: 12px;
    color: #757575;
    font-weight: 500;
  }
</style>
